import { Link } from "react-router-dom";
import { ArrowDown, CreditCard } from "lucide-react";

export default function TrialExpiredLockView() {
  return (
    <div
      className="min-h-[calc(100vh-56px)] w-full flex flex-col justify-between select-none font-sans relative overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #6AA5E6 0%, #84B9EE 30%, #B0D4F9 60%, #DCEDFD 82%, #FFFFFF 100%)",
      }}
      data-testid="trial-expired-locked-screen"
    >
      {/* ── Main Center Scene Matching 404 Exactly ── */}
      <main className="relative z-20 flex-1 flex flex-col items-center justify-center px-4 py-8">
        <div className="relative flex flex-col items-center justify-center max-w-4xl w-full">

          {/* ── Giant 4 0 4 Background Layer with Animated Mascot in Center ── */}
          <div className="relative w-full flex items-center justify-center select-none min-h-[260px] sm:min-h-[340px] md:min-h-[400px]">
            {/* The Huge "4 0 4" Numerals matching NotFound.jsx */}
            <div className="flex items-center justify-center w-full pointer-events-none text-white font-black tracking-widest text-[140px] sm:text-[220px] md:text-[300px] leading-none select-none drop-shadow-sm opacity-90">
              <span className="transform -translate-x-4 sm:-translate-x-8">4</span>
              <span className="transform translate-x-0 opacity-75 scale-95">0</span>
              <span className="transform translate-x-4 sm:translate-x-8">4</span>
            </div>

            {/* ── Animated Center Mascot Character Standing In Front of "0" ── */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div
                className="relative flex flex-col items-center justify-center pointer-events-auto"
                style={{
                  animation: "mascotHover 3.5s ease-in-out infinite",
                }}
              >
                {/* 3D Character Image Render with High Fidelity */}
                <img
                  src="/character-404-transparent.png"
                  alt="Trial Expired Mascot"
                  className="w-56 h-56 sm:w-72 sm:h-72 md:w-80 md:h-80 object-contain drop-shadow-2xl transition-transform"
                  onError={(e) => {
                    e.currentTarget.src = "/character-404.jpg";
                  }}
                />

                {/* Animated Directional Pointer Pointing Downwards towards Button */}
                <div
                  className="absolute -bottom-2 flex flex-col items-center pointer-events-none"
                  style={{
                    animation: "pointerPulse 1.8s ease-in-out infinite",
                  }}
                >
                  <div className="w-8 h-8 rounded-full bg-white/95 text-blue-600 flex items-center justify-center shadow-lg border border-blue-100">
                    <ArrowDown className="w-4 h-4 stroke-[3]" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Clean Descriptive Headline Matching 404 Typography ── */}
          <div className="relative z-30 flex flex-col items-center text-center mt-3 sm:mt-5 max-w-md">
            <h2 className="heading-link font-montserrat-h2 text-slate-900 font-bold text-2xl sm:text-3xl mb-1.5 tracking-tight">
              <Link to="/billing" className="hover:text-blue-600 transition-colors">
                Your 7-day free trial has expired
              </Link>
            </h2>

            <p className="text-xs sm:text-sm text-slate-600 font-normal mb-5 leading-relaxed">
              All workspace options remain visible in your sidebar, but features are paused until you choose a subscription plan.
            </p>

            {/* Clean Pill Button Matching 404 Aesthetic */}
            <div className="relative group">
              <Link
                to="/billing"
                data-testid="trial-expired-upgrade-btn"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-white hover:bg-slate-50 text-slate-800 hover:text-blue-600 text-xs sm:text-sm font-semibold shadow-sm hover:shadow-md border border-slate-200 transition-all duration-200 cursor-pointer"
              >
                <CreditCard className="w-4 h-4 text-blue-600" />
                <span>Choose a subscription plan</span>
                <span className="text-slate-400 group-hover:translate-x-1 transition-transform text-xs">&rarr;</span>
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* ── Soft Cloud Base At Bottom Matching 404 Exactly ── */}
      <div className="relative z-10 w-full pointer-events-none select-none h-16 sm:h-24 overflow-hidden">
        <svg
          viewBox="0 0 1440 160"
          className="w-full h-full fill-white"
          preserveAspectRatio="none"
        >
          <path d="M0,96L48,112C96,128,192,160,288,154.7C384,149,480,107,576,96C672,85,768,107,864,122.7C960,139,1056,149,1152,144C1248,139,1344,117,1392,106.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" />
        </svg>
      </div>

      {/* Inline styles for 404 Mascot animation */}
      <style>{`
        @keyframes mascotHover {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-10px) rotate(-1deg);
          }
        }
        @keyframes pointerPulse {
          0%, 100% {
            transform: translateY(0px) scale(1);
            opacity: 0.9;
          }
          50% {
            transform: translateY(6px) scale(1.08);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
